import React, { useState, useEffect, useCallback } from 'react';
import { useRole } from '../context/RoleContext';
import { useNavigation } from './LeftSidebar';
import {
  Bell,
  Trophy,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Clock,
  FileText,
  Inbox,
  ArrowLeft,
  RefreshCw,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const iconMap = {
  'trophy': Trophy,
  'check-circle': CheckCircle,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'clock': Clock,
  'file-text': FileText,
  'inbox': Inbox,
  'bell': Bell
};

// Color mapping for light mode (student) - minimalist, no backgrounds
const colorMapLight = {
  'green': {
    icon: 'text-emerald-500',
    text: 'text-emerald-600'
  },
  'blue': {
    icon: 'text-blue-500',
    text: 'text-blue-600'
  },
  'orange': {
    icon: 'text-amber-500',
    text: 'text-amber-600'
  },
  'red': {
    icon: 'text-red-500',
    text: 'text-red-600'
  }
};

// Color mapping for dark mode (professor) - minimalist, no backgrounds
const colorMapDark = {
  'green': {
    icon: 'text-emerald-400',
    text: 'text-emerald-400'
  },
  'blue': {
    icon: 'text-blue-400',
    text: 'text-blue-400'
  },
  'orange': {
    icon: 'text-amber-400',
    text: 'text-amber-400'
  },
  'red': {
    icon: 'text-red-400',
    text: 'text-red-400'
  }
};

// Time ago formatter
const formatTimeAgo = (dateString) => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
};

// Notification Card Component - Minimalist design
const NotificationCard = ({ notification, onClick, isProfessor }) => {
  const IconComponent = iconMap[notification.icon] || Bell;
  const colorMap = isProfessor ? colorMapDark : colorMapLight;
  const colors = colorMap[notification.color] || colorMap.blue;
  
  return (
    <button
      onClick={() => onClick(notification)}
      className={`w-full text-left py-4 px-2 transition-all duration-200 border-b
        ${isProfessor 
          ? 'border-white/10 hover:bg-white/5' 
          : 'border-gray-100 hover:bg-gray-50'}
      `}
      data-testid={`notification-${notification.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon - no background */}
        <div className="pt-0.5 shrink-0">
          <IconComponent className={`w-5 h-5 ${colors.icon}`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className={`font-medium text-sm ${isProfessor ? 'text-white' : 'text-gray-900'}`}>
              {notification.title}
            </h3>
            <span className={`text-xs shrink-0 ${isProfessor ? 'text-gray-500' : 'text-gray-400'}`}>
              {formatTimeAgo(notification.created_at)}
            </span>
          </div>
          <p className={`text-sm mt-0.5 ${isProfessor ? 'text-gray-400' : 'text-gray-500'}`}>
            {notification.message}
          </p>
          
          {/* Action hint */}
          {notification.action && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${colors.text}`}>
              <span>
                {notification.action === 'grade' ? 'Calificar ahora' : 
                 notification.action === 'answer' ? 'Responder reto' : 'Ver detalles'}
              </span>
              <ChevronRight className="w-3 h-3" />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// Empty State Component
const EmptyState = ({ isProfessor }) => (
  <div className={`flex flex-col items-center justify-center py-16 px-4 text-center
    ${isProfessor ? 'text-gray-400' : 'text-gray-500'}`}
  >
    <Bell className={`w-12 h-12 mb-4 ${isProfessor ? 'text-gray-600' : 'text-gray-300'}`} />
    <h3 className={`text-lg font-medium mb-2 ${isProfessor ? 'text-white' : 'text-gray-900'}`}>
      No hay notificaciones
    </h3>
    <p className="text-sm max-w-xs">
      {isProfessor 
        ? 'Las notificaciones sobre entregas y retos aparecerán aquí.'
        : 'Cuando tengas nuevos retos o calificaciones, te avisaremos aquí.'}
    </p>
  </div>
);

// Main Notifications View
const NotificationsView = ({ onOpenChallenge }) => {
  const { isProfessor } = useRole();
  const { setActiveNav, markNotificationsSeen } = useNavigation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const studentId = 's2a1'; // Carlos Mendoza

  const fetchNotifications = useCallback(async () => {
    try {
      const endpoint = isProfessor 
        ? `${API_URL}/api/notifications/professor`
        : `${API_URL}/api/notifications/student/${studentId}`;
      
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Error al cargar notificaciones');
      
      const data = await response.json();
      setNotifications(data.notifications || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('No se pudieron cargar las notificaciones');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isProfessor]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark notifications as seen when component mounts and notifications are loaded
  useEffect(() => {
    if (!loading && notifications.length >= 0) {
      // Small delay to ensure the user actually sees the notifications
      const timer = setTimeout(() => {
        markNotificationsSeen(isProfessor);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, notifications.length, isProfessor, markNotificationsSeen]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchNotifications();
  };

  const handleNotificationClick = (notification) => {
    // Navigate based on notification type
    if (notification.challenge_id) {
      if (isProfessor) {
        // Go to professor results view
        setActiveNav('profesor-resultados');
      } else {
        // For students - if it's a new challenge or pending, open directly to answer
        if (notification.type === 'new_challenge' || notification.type === 'challenge_expiring') {
          // Use callback to open challenge directly
          if (onOpenChallenge) {
            onOpenChallenge(notification.challenge_id);
          } else {
            // Store the challenge ID to open and navigate
            sessionStorage.setItem('openChallengeId', notification.challenge_id);
            setActiveNav('student-retos');
          }
        } else {
          // For other types, just go to the challenges list
          setActiveNav('student-retos');
        }
      }
    }
  };

  const handleBack = () => {
    setActiveNav('chat');
  };

  // Sort all notifications by date (most recent first) - no grouping
  const sortedNotifications = [...notifications].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });

  return (
    <div 
      className={`h-full flex flex-col ${isProfessor ? 'text-white' : ''}`} 
      data-testid="notifications-view"
    >
      {/* Header - Fixed */}
      <div className={`shrink-0 px-4 py-4 border-b ${isProfessor ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className={`p-2 -ml-2 rounded-lg transition-colors
                ${isProfessor ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Bell className={`w-5 h-5 ${isProfessor ? 'text-blue-400' : 'text-gray-700'}`} />
              <h1 className={`text-lg font-semibold ${isProfessor ? 'text-white' : 'text-gray-900'}`}>
                Notificaciones
              </h1>
            </div>
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`p-2 rounded-lg transition-colors
              ${isProfessor ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}
              ${refreshing ? 'opacity-50' : ''}`}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {loading ? (
            <div className="space-y-4 py-4">
              {[1, 2, 3].map(i => (
                <div 
                  key={i} 
                  className={`h-16 rounded-lg animate-pulse
                    ${isProfessor ? 'bg-white/5' : 'bg-gray-100'}`} 
                />
              ))}
            </div>
          ) : error ? (
            <div className={`text-center py-8 ${isProfessor ? 'text-red-400' : 'text-red-500'}`}>
              <AlertCircle className="w-8 h-8 mx-auto mb-2" />
              <p>{error}</p>
              <button 
                onClick={handleRefresh}
                className="mt-4 text-sm underline"
              >
                Reintentar
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <EmptyState isProfessor={isProfessor} />
          ) : (
            <div className={`rounded-xl overflow-hidden ${isProfessor ? 'bg-white/5' : 'bg-white'}`}>
              {sortedNotifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                  isProfessor={isProfessor}
                />
              ))}
            </div>
          )}
          
          {/* Summary footer */}
          {!loading && notifications.length > 0 && (
            <div className={`mt-8 text-center text-xs
              ${isProfessor ? 'text-gray-600' : 'text-gray-400'}`}
            >
              {notifications.length} notificación{notifications.length !== 1 ? 'es' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsView;
